<template>
  <v-container fluid class="pa-0 mt-16">
    <v-card class="mx-auto" max-width="1200">
      <v-card-title class="d-flex justify-space-between align-center">
        <div>
          <div class="text-h5 font-weight-bold">{{ course.title }}</div>
          <div class="text-subtitle-2 grey--text" v-if="currentContent">
            Module {{ getCurrentModule?.order }}: {{ getCurrentModule?.name }}
          </div>
        </div>
        <div class="d-flex align-center">
          <!-- Add Module and Add Module Content buttons for Admin -->
          <div v-if="getUserRole() === 'Admin'" class="mr-4">

             <v-btn
              color="primary"
              @click="generatePDF"
              class="mr-2"
            >
                <v-icon left>mdi-download</v-icon>Download Content
            </v-btn>


            <v-btn
              color="primary"
              @click="showAddModuleDialog = true"
              class="mr-2"
            >
              <v-icon left>mdi-plus</v-icon>Add Module
            </v-btn>
            <v-btn color="secondary" @click="showAddContentDialog = true">
              <v-icon left>mdi-file-plus</v-icon>Add Module Content
            </v-btn>
          </div>
          <v-btn icon @click="handlePrevious" :disabled="!hasPreviousContent">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="mx-2 grey--text">
            {{ activeContentIndex + 1 }} / {{ allContent.length }}
          </span>
          <v-btn icon @click="handleNext" :disabled="!hasNextContent">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <!-- <v-btn
            icon
            @click="handlePrevious"
            :disabled="activeContentIndex === 0"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="mx-2 grey--text">
            {{ activeContentIndex + 1 }} / {{ allContent.length }}
          </span>
          <v-btn
            icon
            @click="handleNext"
            :disabled="activeContentIndex === allContent.length - 1"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn> -->
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <v-row no-gutters>
        <!-- Navigation Sidebar -->
        <v-col cols="3" class="border-r">
          <v-list dense class="py-0">
            <template v-for="module in course.modules">
              <v-list-group :key="module.moduleId" :value="true" no-action>
                <template v-slot:activator>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ module.name }}
                      <v-spacer></v-spacer>
                      <div v-if="getUserRole() === 'Admin'" class="ml-2">
                        <v-btn
                          icon
                          small
                          class="mr-1"
                          @click.stop="openEditModule(module)"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click.stop="
                            openDeleteDialog('module', module.moduleId)
                          "
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </div>
                    </v-list-item-title>
                  </v-list-item-content>
                </template>

                <v-list-item
                  v-for="content in module.content"
                  :key="content.moduleContentId"
                  @click="navigateToContent(content)"
                  :class="{ 'primary lighten-4': isCurrentContent(content) }"
                >
                  <v-list-item-icon>
                    <v-icon :color="getContentTypeIcon(content.type).color">
                      {{ getContentTypeIcon(content.type).icon }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-subtitle-2">
                      {{ content.title }}
                      <v-spacer></v-spacer>
                      <div v-if="getUserRole() === 'Admin'" class="ml-2">
                        <v-btn
                          icon
                          small
                          class="mr-1"
                          @click.stop="openEditContent(content)"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click.stop="
                            openDeleteDialog('content', content.moduleContentId)
                          "
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </div>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-group>
            </template>
          </v-list>
        </v-col>

        <!-- Main Content Area -->
        <v-col cols="9" class="pa-6">
          <template v-if="!hasContent">
            <empty-state type="content" :courseTitle="course?.title" />
          </template>
          <template v-else>
            <div
              v-if="isCourseCompletionPage"
              class="course-completion-wrapper text-center"
            >
              <v-icon size="100" color="success" class="mb-6"
                >mdi-check-circle-outline</v-icon
              >
              <h2 class="text-h4 mb-4">{{ $t("courseCompleted") }}</h2>
              <p class="text-subtitle-1 mb-6">
                {{ $t("readyForAssessment") }}
              </p>

              <!-- Quiz List for Assessment -->
              <v-row
                v-if="availableQuizzes.length"
                justify="center"
                class="assessment-grid"
              >
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                  v-for="quiz in availableQuizzes"
                  :key="quiz.quizTopicId"
                  class="mb-4"
                >
                  <v-card
                    color="grey lighten-3"
                    shaped
                    elevation="6"
                    height="100%"
                    class="d-flex flex-column"
                  >
                    <v-card-text class="pb-0 flex-grow-1">
                      <div class="text-h6 font-weight-bold mb-3 text-truncate">
                        {{ quiz.quizTitle }}
                      </div>
                      <v-list class="pa-0">
                        <v-list-item class="py-1">
                          <v-list-item-icon class="mr-3">
                            <v-icon>mdi-timer</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="body-2"
                              >Time</v-list-item-title
                            >
                            <v-list-item-subtitle>
                              {{
                                quiz.quizTime
                                  ? quiz.quizTime + " minutes"
                                  : "N/A"
                              }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item class="py-1">
                          <v-list-item-icon class="mr-3">
                            <v-icon>mdi-grade</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="body-2"
                              >Total Marks</v-list-item-title
                            >
                            <v-list-item-subtitle>
                              {{ quiz.quizTotalMarks || "N/A" }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item class="py-1">
                          <v-list-item-icon class="mr-3">
                            <v-icon>mdi-help-circle</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="body-2"
                              >Questions</v-list-item-title
                            >
                            <v-list-item-subtitle>
                              {{ quiz.numberOfQuestions }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions class="justify-center pa-3">
                      <v-btn
                        color="primary"
                        @click="initializeQuiz(quiz)"
                        block
                        class="text-capitalize"
                        :disabled="quiz.numberOfQuestions <= 0"
                      >
                        Start Assessment
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>

              <v-alert v-else type="info" class="mt-4">
                {{ $t("noAssessmentAvailable") }}
              </v-alert>
            </div>

            <div v-else-if="currentContent" class="content-wrapper">
              <h2 class="text-h6 mb-4">{{ currentContent.title }}</h2>

              <!-- Display URL if it exists -->
              <v-alert
                v-if="currentContent.contentUrl"
                type="info"
                class="mb-4"
                outlined
              >
                <div class="d-flex align-center">
                  <v-icon left>mdi-link</v-icon>
                  <a
                    :href="currentContent.contentUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ currentContent.contentUrl }}
                  </a>
                </div>
              </v-alert>

              <!-- Audio Content -->
              <template v-if="currentContent.type === 1">
                <div v-if="hasAudioFiles" class="audio-wrapper mb-4">
                  <audio
                    v-for="file in getFilesByType('audio')"
                    :key="file.id"
                    controls
                    class="audio-player mb-2"
                  >
                    <source
                      :src="
                        config.hostname +
                        '/Resources/ModuleFiles/' +
                        file.filePath
                      "
                      :type="file.contentType"
                    />
                    Your browser does not support the audio tag.
                  </audio>
                </div>
              </template>

              <!-- Video Content -->
              <template v-if="currentContent.type === 0">
                <div
                  v-if="currentContent.contentUrl"
                  class="video-wrapper mb-4"
                >
                  <iframe
                    :src="getYoutubeEmbedUrl(currentContent.contentUrl)"
                    class="video-frame"
                    allowfullscreen
                  ></iframe>
                </div>
                <div v-else-if="hasVideoFiles" class="video-wrapper mb-4">
                  <video
                    v-for="file in getFilesByType('video')"
                    :key="file.id"
                    controls
                    class="video-frame mb-2"
                  >
                    <source
                      :src="
                        config.hostname +
                        '/Resources/ModuleFiles/' +
                        file.filePath
                      "
                      :type="file.contentType"
                    />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </template>

              <!-- PDF Viewers -->
              <template v-if="hasPDFFiles">
                <v-card
                  v-for="file in getFilesByType('application/pdf')"
                  :key="file.id"
                  outlined
                  class="mb-4"
                >
                  <div class="d-flex align-center pa-2">
                    <span class="text-subtitle-2">{{ file.fileName }}</span>
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      color="primary"
                      :href="
                        config.hostname +
                        '/Resources/ModuleFiles/' +
                        file.filePath
                      "
                      target="_blank"
                    >
                      <v-icon left>mdi-open-in-new</v-icon>
                      Open in New Tab
                    </v-btn>
                  </div>
                  <iframe
                    :src="
                      config.hostname +
                      '/Resources/ModuleFiles/' +
                      file.filePath
                    "
                    class="pdf-frame"
                  ></iframe>
                </v-card>
              </template>

              <!-- PowerPoint Downloads -->
              <template v-if="hasPowerPointFiles">
                <v-card outlined class="pa-4 mb-4">
                  <div class="d-flex flex-wrap">
                    <div
                      v-for="file in getPowerPointFiles"
                      :key="file.id"
                      class="text-center ma-2"
                    >
                      <v-icon size="48" color="primary"
                        >mdi-file-presentation-box</v-icon
                      >
                      <div class="mt-2">
                        <div class="text-subtitle-2 mb-1">
                          {{ file.fileName }}
                        </div>
                        <v-btn
                          color="primary"
                          :href="
                            config.hostname +
                            '/Resources/ModuleFiles/' +
                            file.filePath
                          "
                          target="_blank"
                        >
                          <v-icon left>mdi-download</v-icon>
                          Download
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </v-card>
              </template>

              <template v-if="hasImageFiles">
                <v-card
                  v-for="file in getFilesByType('image')"
                  :key="file.id"
                  outlined
                  class="mb-4"
                >
                  <v-card-title class="d-flex justify-space-between">
                    <span class="text-subtitle-2">{{ file.fileName }}</span>
                    <v-btn
                      icon
                      color="primary"
                      :href="
                        config.hostname +
                        '/Resources/ModuleFiles/' +
                        file.filePath
                      "
                      target="_blank"
                    >
                      <v-icon>mdi-open-in-new</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <div class="image-wrapper">
                    <img
                      :src="
                        config.hostname +
                        '/Resources/ModuleFiles/' +
                        file.filePath
                      "
                      class="image-frame"
                      :alt="file.fileName"
                      @click="openImageFullScreen(file)"
                    />
                  </div>
                </v-card>
              </template>

              <!-- Text Content -->
              <!-- <template v-if="currentContent.textContent">
      <v-card outlined class="pa-4">
        <div class="text-body-1">
          {{ currentContent.textContent }}
        </div>
      </v-card>
    </template> -->
              <template v-if="currentContent.textContent">
                <v-card outlined class="pa-4">
                  <div
                    class="text-body-1"
                    v-html="currentContent.textContent"
                  ></div>
                </v-card>
              </template>
            </div>
          </template>
        </v-col>
      </v-row>
    </v-card>

    <!-- Add Module Dialog -->
    <v-dialog v-model="showAddModuleDialog" max-width="500">
      <v-card>
        <v-card-title>Add New Module</v-card-title>
        <v-card-text>
          <v-alert v-if="errorMessage" type="error" dense text class="mb-4">
            {{ errorMessage }}
          </v-alert>
          <v-form ref="moduleForm" v-model="moduleFormValid">
            <v-text-field
              v-model="newModule.name"
              label="Module Name"
              :rules="[(v) => !!v || 'Module name is required']"
              required
              :disabled="isCreatingModule"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="showAddModuleDialog = false"
            :disabled="isCreatingModule"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="createModule"
            :disabled="!moduleFormValid || isCreatingModule"
            :loading="isCreatingModule"
          >
            Create Module
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Module Content Dialog -->
    <v-dialog v-model="showAddContentDialog" max-width="600">
      <v-card>
        <v-card-title>Add Module Content</v-card-title>
        <v-card-text>
          <v-alert v-if="errorMessage" type="error" dense text class="mb-4">
            {{ errorMessage }}
          </v-alert>

          <v-form ref="contentForm" v-model="contentFormValid">
            <v-select
              v-model="newContent.moduleId"
              :items="course.modules"
              item-text="name"
              item-value="moduleId"
              label="Select Module"
              :rules="[(v) => !!v || 'Module is required']"
              required
              :disabled="uploading"
            ></v-select>

            <v-text-field
              v-model="newContent.title"
              label="Content Title"
              :rules="[(v) => !!v || 'Title is required']"
              required
              :disabled="uploading"
            ></v-text-field>

            <v-select
              v-model="newContent.type"
              :items="contentTypes"
              item-text="name"
              item-value="value"
              label="Content Type"
              :rules="[(v) => v !== null || 'Type is required']"
              required
              :disabled="uploading"
            ></v-select>

            <v-file-input
              v-if="showFileUpload"
              v-model="newContent.files"
              :rules="multipleFileRules"
              :accept="getAcceptedFileTypes"
              label="Upload Files"
              prepend-icon="mdi-file-upload"
              show-size
              multiple
              chips
              truncate-length="25"
              @change="handleFileChange"
              :disabled="uploading"
              :loading="uploading"
            ></v-file-input>

            <!-- Preview section for uploaded files -->
            <v-list
              v-if="showFileUpload && newContent.uploadedFiles.length"
              dense
              class="mt-2"
            >
              <v-subheader>Uploaded Files</v-subheader>
              <v-list-item
                v-for="(file, index) in newContent.uploadedFiles"
                :key="index"
              >
                <v-list-item-icon>
                  <v-icon>mdi-file</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ file.fileName }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn
                    icon
                    small
                    @click="removeUploadedFile(index)"
                    :disabled="uploading"
                  >
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <!-- URL input - show only for Link type -->
            <v-text-field
              v-if="newContent.type === 2"
              v-model="newContent.contentUrl"
              label="Content URL"
              :rules="[(v) => !!v || 'URL is required for Link type']"
              required
              :disabled="uploading"
            ></v-text-field>

            <v-text-field
              v-if="newContent.type === 0"
              v-model="newContent.contentUrl"
              label="YouTube URL (Optional if video file is uploaded)"
              :rules="videoUrlRules"
              :disabled="uploading"
              :required="!hasUploadedVideoFiles"
            ></v-text-field>

            <!-- <v-textarea
              v-model="newContent.textContent"
              label="Text Content"
              v-if="newContent.type === 4 || newContent.type === 3" 
              :disabled="uploading"
            ></v-textarea> -->

            <enhanced-text-editor
              v-if="newContent.type === 4"
              v-model="newContent.textContent"
              :uploading="uploading"
              :type="newContent.type"
              @change="onTextContentChange"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeContentDialog" :disabled="uploading">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="createModuleContent"
            :disabled="!contentFormValid || uploading"
            :loading="uploading"
          >
            Create Content
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Quiz Instructions Dialog -->
    <v-dialog
      v-model="quizInstructionsDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-toolbar dark>
          <v-btn icon dark @click="quizInstructionsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <v-toolbar-title>{{ $t("instructions") }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn text class="text-capitalize" @click="continueQuiz()">
              <h3>{{ $t("continue") }}</h3>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-timeline align-top dense>
          <v-timeline-item
            small
            dark
            v-for="item in quizInstructions"
            :key="item.instructionId"
          >
            {{ item.description }}
          </v-timeline-item>
        </v-timeline>
      </v-card>
    </v-dialog>

    <!-- Edit Module Dialog -->
    <v-dialog v-model="showEditModuleDialog" max-width="500">
      <v-card>
        <v-card-title>Edit Module</v-card-title>
        <v-card-text>
          <v-alert v-if="errorMessage" type="error" dense text class="mb-4">
            {{ errorMessage }}
          </v-alert>
          <v-form ref="editModuleForm" v-model="editModuleFormValid">
            <v-text-field
              v-model="editingModule.name"
              label="Module Name"
              :rules="[(v) => !!v || 'Module name is required']"
              required
              :disabled="isUpdatingModule"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="showEditModuleDialog = false"
            :disabled="isUpdatingModule"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="updateModule"
            :disabled="!editModuleFormValid || isUpdatingModule"
            :loading="isUpdatingModule"
          >
            Update Module
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Module Content Dialog -->
    <v-dialog v-model="showEditContentDialog" max-width="600">
      <v-card>
        <v-card-title>Edit Module Content</v-card-title>
        <v-card-text>
          <v-alert v-if="errorMessage" type="error" dense text class="mb-4">
            {{ errorMessage }}
          </v-alert>
          <v-form ref="editContentForm" v-model="editContentFormValid">
            <v-select
              v-model="editingContent.moduleId"
              :items="course.modules"
              item-text="name"
              item-value="moduleId"
              label="Select Module"
              :rules="[(v) => !!v || 'Module is required']"
              required
              :disabled="uploading"
            ></v-select>

            <v-text-field
              v-model="editingContent.title"
              label="Content Title"
              :rules="[(v) => !!v || 'Title is required']"
              required
              :disabled="uploading"
            ></v-text-field>

            <v-text-field
              v-if="editingContent.type === 0"
              v-model="editingContent.contentUrl"
              label="YouTube URL (Optional if video file is uploaded)"
              :rules="videoUrlRules"
              :disabled="uploading"
            ></v-text-field>

            <!-- <v-textarea
              v-model="editingContent.textContent"
              label="Text Content"
              v-if="editingContent.type === 4 || editingContent.type === 3" 
              :disabled="uploading"
            ></v-textarea> -->
            <enhanced-text-editor
              v-if="editingContent.type === 4"
              v-model="editingContent.textContent"
              :uploading="uploading"
              :type="editingContent.type"
              @change="onTextContentChange"
            />

            <!-- Existing files display -->
            <v-list
              v-if="editingContent.contentFiles?.length"
              dense
              class="mt-2"
            >
              <v-subheader>Current Files</v-subheader>
              <v-list-item
                v-for="file in editingContent.contentFiles"
                :key="file.id"
              >
                <v-list-item-icon>
                  <v-icon>mdi-file</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ file.fileName }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn
                    icon
                    small
                    @click="removeContentFile(file)"
                    :disabled="uploading"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <!-- New files upload -->
            <v-file-input
              v-if="showFileUpload"
              v-model="editingContent.newFiles"
              :rules="multipleFileRules"
              :accept="getAcceptedFileTypes"
              label="Upload New Files"
              prepend-icon="mdi-file-upload"
              show-size
              multiple
              chips
              truncate-length="25"
              @change="handleFileChange"
              :disabled="uploading"
              :loading="uploading"
            ></v-file-input>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeEditContentDialog" :disabled="uploading">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="updateModuleContent"
            :disabled="!editContentFormValid || uploading"
            :loading="uploading"
          >
            Update Content
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this {{ deleteType }}? This action
          cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="showDeleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" @click="confirmDelete" :loading="isDeleting">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import EmptyState from "./EmptyState.vue";
import config from "../../../public/config";
import EnhancedTextEditor from "./TextEditor.vue";
import Vue from "vue";
import VueToast from "vue-toastification";

import "vue-toastification/dist/index.css";
import jsPDF from 'jspdf'
import 'jspdf-autotable'

const options = {
  position: "top-right",
  timeout: 5000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: true,
  closeButton: "button",
  icon: true,
  rtl: false,
};

Vue.use(VueToast, options);

export default {
  name: "CourseContentViewer",
  components: {
    EmptyState,
    EnhancedTextEditor,
  },

  data() {
    return {
      config,
      activeContentIndex: 0,
      course: null,
      showAddModuleDialog: false,
      showAddContentDialog: false,
      moduleFormValid: false,
      contentFormValid: false,
      uploading: false,
      newModule: {
        courseId: null,
        name: "",
      },
      newContent: {
        moduleId: null,
        title: "",
        contentUrl: "",
        textContent: "",
        type: null,
        description: "",
        files: [], // Changed from filePath to files array
        uploadedFiles: [], // To store uploaded file results
      },
      contentTypes: [
        { name: "Video", value: 0 },
        { name: "Audio", value: 1 },
        { name: "Link", value: 2 },
        { name: "Image", value: 3 },
        { name: "Text", value: 4 },
        { name: "Document", value: 5 },
      ],
      fileRules: [
        (value) =>
          !value ||
          value.size < 15 * 1024 * 1024 ||
          "File size should be less than 15MB!",
        (value) =>
          !value ||
          this.validateFileType(value) ||
          "Invalid file format. Please upload a PDF, Video, or PPT file",
      ],
      videoUrlRules() {
        return [
          (v) => {
            if (this.hasUploadedVideoFiles) {
              return true;
            }

            return (
              !!v || "YouTube URL is required when no video file is uploaded"
            );
          },
        ];
      },
      // Modify multipleFileRules to include video validation
      multipleFileRules: [
        (files) =>
          !files.length ||
          files.every((file) => file.size < 15 * 1024 * 1024) ||
          "Each file size should be less than 15MB!",
        (files) => {
          if (!files.length) return true;

          // Check file types based on content type
          if (this.newContent.type === 0) {
            // Video
            return (
              files.every((file) => file.type.startsWith("video/")) ||
              "Please upload valid video files"
            );
          } else if (this.newContent.type === 1) {
            // Audio
            return (
              files.every((file) => file.type.startsWith("audio/")) ||
              "Please upload valid audio files"
            );
          } else if (this.newContent.type === 3) {
            // Image
            return (
              files.every((file) => file.type.startsWith("image/")) ||
              "Please upload valid image files"
            );
          } else {
            return (
              files.every((file) => this.validateFileType(file)) ||
              "Invalid file format"
            );
          }
        },
      ],
      availableQuizzes: [],
      quizInstructions: [],
      quizInstructionsDialog: false,
      currentQuiz: null,

      showEditModuleDialog: false,
      showEditContentDialog: false,
      showDeleteDialog: false,
      editModuleFormValid: false,
      editContentFormValid: false,
      isUpdatingModule: false,
      isDeleting: false,
      deleteType: "", // 'module' or 'content'
      deleteItemId: null,
      editingModule: {
        moduleId: null,
        name: "",
      },
      editingContent: {
        moduleContentId: null,
        moduleId: null,
        title: "",
        contentUrl: "",
        textContent: "",
        type: null,
        contentFiles: [],
        newFiles: [],
        uploadedFiles: [],
      },
    };
  },

  computed: {
    getAcceptedFileTypes() {
      switch (this.newContent.type) {
        case 0: // Video
          return ".mp4,.mov,.avi";
        case 1: // Audio
          return ".mp3,.wav,.ogg";
        case 3: // Image
          return ".jpg,.jpeg,.png,.gif,.webp,.svg";
        default: // PDF and PowerPoint
          return ".pdf,.ppt,.pptx";
      }
    },
    hasAudioFiles() {
      return this.currentContent?.contentFiles?.some((file) =>
        file.contentType.startsWith("audio/")
      );
    },
    hasVideoFiles() {
      return this.currentContent?.contentFiles?.some((file) =>
        file.contentType.startsWith("video/")
      );
    },

    allContent() {
      const baseContent =
        this.course?.modules?.reduce((acc, module) => {
          if (!module.content || module.content.length === 0) return acc;

          const moduleContent = [...module.content]
            .sort((a, b) => a.order - b.order)
            .map((content) => ({
              ...content,
              moduleName: module.name,
              moduleOrder: module.order,
              moduleId: module.moduleId,
            }));

          return acc.concat(moduleContent);
        }, []) || [];

      // Only add completion page if there is actual content
      return baseContent.length > 0
        ? [
            ...baseContent,
            {
              type: "course-completion",
              title: "Course Completed",
              moduleContentId: "completion-page",
            },
          ]
        : [];
    },

    hasContent() {
      return (
        this.course?.modules?.some(
          (module) => module.content && module.content.length > 0
        ) ?? false
      );
    },

    isCourseCompletionPage() {
      const currentContent = this.allContent[this.activeContentIndex];
      return currentContent?.type === "course-completion";
    },

    hasUploadedVideoFiles() {
      return this.newContent.uploadedFiles.some((file) =>
        file.contentType.startsWith("video/")
      );
    },

    hasImageFiles() {
      return this.currentContent?.contentFiles?.some((file) =>
        file.contentType.startsWith("image/")
      );
    },

    hasPDFFiles() {
      return this.currentContent?.contentFiles?.some(
        (file) => file.contentType === "application/pdf"
      );
    },

    hasPowerPointFiles() {
      return this.currentContent?.contentFiles?.some((file) =>
        file.contentType.includes("presentation")
      );
    },

    getPowerPointFiles() {
      return (
        this.currentContent?.contentFiles?.filter((file) =>
          file.contentType.includes("presentation")
        ) ?? []
      );
    },

    currentContent() {
      return this.allContent[this.activeContentIndex] || null;
    },

    getCurrentModule() {
      if (!this.currentContent || !this.course?.modules) return null;
      return this.course.modules.find(
        (module) => module.moduleId === this.currentContent.moduleId
      );
    },

    hasNextContent() {
      return this.activeContentIndex < this.allContent.length - 1;
    },

    hasPreviousContent() {
      return this.activeContentIndex > 0;
    },

    showFileUpload() {
      return this.newContent.type !== 2 && this.newContent.type !== 4;
    },
  },

  created() {
    this.userInfo = this.$store.getters["dashboard/userInfo"];
    this.fetchCourseDetails();
  },

  methods: {
    async createModule() {
      if (!this.$refs.moduleForm.validate()) return;

      this.isCreatingModule = true;
      this.errorMessage = "";

      try {
        this.newModule.courseId = this.$route.params.courseId;
        await this.$store.dispatch("course/createModule", this.newModule);

        await this.fetchCourseDetails();
        this.$refs.moduleForm.reset();
        this.showAddModuleDialog = false;
        this.$toast.success("Module created successfully");
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);
        this.errorMessage = errorMessage;
        this.$toast.error(`Failed to create module: ${errorMessage}`);
        console.error("Error creating module:", error);
      } finally {
        this.isCreatingModule = false;
      }
    },

    getErrorMessage(error) {
      console.error("Error:", error);
      if (!error) return "An unexpected error occurred";

      if (error.response) {
        if (error.response && error.response.status === 413) {
          return "The file is too large. Please upload a file smaller than 15MB.";
        }
        if (error.response.data?.message) {
          return error.response.data.message;
        }
        if (error.response.data) {
          return typeof error.response.data === "string"
            ? error.response.data
            : "Server error occurred";
        }
        return `Server error: ${error.response.status}`;
      }

      if (error.message) {
        return error.message;
      }
      return "An unexpected error occurred";
    },

    async handleFileChange(files) {
      if (!files || !files.length || !this.showFileUpload) {
        this.newContent.uploadedFiles = [];
        return;
      }

      // Add file size validation before upload
      const oversizedFiles = files.filter(
        (file) => file.size >= 15 * 1024 * 1024
      );
      if (oversizedFiles.length > 0) {
        this.errorMessage = "Files must be smaller than 15MB";
        if (this.$toast) {
          this.$toast.error("One or more files exceed the 15MB size limit");
        }
        this.newContent.files = [];
        return;
      }

      this.uploading = true;
      this.errorMessage = "";

      try {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append("files", file);
        });

        const response = await this.$store.dispatch(
          "course/uploadModuleFiles",
          formData
        );

        console.log("Upload response:", response); // Debug log

        if (response?.status === 200 && response?.data?.fileNames) {
          // Store the uploaded file information
          this.newContent.uploadedFiles = response.data.fileNames.map(filePath => ({
            filePath: filePath.replace(/\\/g, '/'),
            fileName: filePath.split('\\').pop(), 
            contentType: this.getContentTypeFromFile(filePath)
          }));
          
          this.$toast.success("Files uploaded successfully");
        } else {
          throw new Error("Invalid response format from server");
        }
      } catch (error) {
        let errorMessage = this.getErrorMessage(error);

        // Handle 413 Payload Too Large specifically
        if (error.response && error.response.status === 413) {
          errorMessage =
            "The file is too large. Please upload a smaller file or check the server's file size limit.";
        }

        this.errorMessage = errorMessage;

        if (this.$toast) {
          this.$toast.error(`Failed to upload files: ${errorMessage}`);
        } else {
          console.error(`Failed to upload files: ${errorMessage}`);
        }

        console.error("Error uploading files:", error);

        this.newContent.files = [];
        this.newContent.uploadedFiles = [];
        if (this.$refs.contentForm) {
          this.$refs.contentForm.resetValidation();
        }
      } finally {
        this.uploading = false;
      }
    },

    getContentTypeFromFile(filePath) {
  const extension = filePath.split('.').pop().toLowerCase();
  const typeMap = {
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    pdf: 'application/pdf',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif'
  };
  return typeMap[extension] || 'application/octet-stream';
},

    async createModuleContent() {
      if (!this.$refs.contentForm.validate()) return;

      // For audio content, check if files are present
      if (this.newContent.type === 1 && !this.newContent.uploadedFiles.length) {
        this.errorMessage = "Audio file is required";
        return;
      }

      this.uploading = true;
      this.errorMessage = "";

      try {
        const contentData = {
          moduleId: this.newContent.moduleId,
          title: this.newContent.title,
          type: this.newContent.type,
          files: this.newContent.uploadedFiles,
          description: this.newContent.description,
          textContent: this.newContent.textContent,
        };

        await this.$store.dispatch("course/createModuleContent", contentData);
        await this.fetchCourseDetails();

        this.resetForm();
        this.$toast.success("Module content created successfully");
        this.showAddContentDialog = false;
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);
        this.errorMessage = errorMessage;
        this.$toast.error(`Failed to create module content: ${errorMessage}`);
        console.error("Error creating module content:", error);
      } finally {
        this.uploading = false;
      }
    },

    async generatePDF() {
  if (!this.course) {
    this.$toast.error('No course data available');
    return;
  }

  // Show loading state
  this.$store.dispatch("dashboard/applyLoading");
  
  try {
    const doc = new jsPDF();
    let yPosition = 20;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const lineHeight = 7;
    const maxWidth = 170;
    
    // Helper function to check if we need a new page
    const checkPageBreak = (requiredHeight) => {
      if (yPosition + requiredHeight > pageHeight - margin) {
        doc.addPage();
        yPosition = 20;
      }
    };
    
    // Helper function to add wrapped text with different styles
    const addWrappedText = (text, x, y, fontSize = 10, style = 'normal', color = [0, 0, 0]) => {
      if (!text) return yPosition;
      
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', style);
      doc.setTextColor(color[0], color[1], color[2]);
      
      const lines = doc.splitTextToSize(text.toString(), maxWidth);
      const totalHeight = lines.length * lineHeight;
      
      checkPageBreak(totalHeight);
      
      lines.forEach((line, index) => {
        doc.text(line, x, yPosition + (index * lineHeight));
      });
      
      yPosition += totalHeight + 3;
      return yPosition;
    };
    
    // Helper function to add a section divider
    const addDivider = () => {
      checkPageBreak(10);
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, 190, yPosition);
      yPosition += 10;
    };
    
    // Course Title
    addWrappedText(this.course.title, margin, yPosition, 18, 'bold', [0, 0, 128]);
    yPosition += 5;
    
    // Course Information Section
    addWrappedText('COURSE INFORMATION', margin, yPosition, 14, 'bold', [64, 64, 64]);
    addDivider();
    
    if (this.course.description) {
      addWrappedText(`Description: ${this.course.description}`, margin, yPosition, 11);
    }
    
    if (this.course.category) {
      addWrappedText(`Category: ${this.course.category}`, margin, yPosition, 10);
    }
    
    yPosition += 10;
    
    // Course Statistics
    const totalModules = this.course.modules?.length || 0;
    const totalContent = this.course.modules?.reduce((acc, module) => acc + (module.content?.length || 0), 0) || 0;
    
    addWrappedText('COURSE OVERVIEW', margin, yPosition, 14, 'bold', [64, 64, 64]);
    addDivider();
    
    addWrappedText(`Total Modules: ${totalModules}`, margin, yPosition, 10);
    addWrappedText(`Total Content Items: ${totalContent}`, margin, yPosition, 10);
    
    yPosition += 15;
    
    // Modules Content
    if (this.course.modules && this.course.modules.length > 0) {
      addWrappedText('COURSE MODULES', margin, yPosition, 14, 'bold', [64, 64, 64]);
      addDivider();
      
      // Sort modules by order
      const sortedModules = [...this.course.modules].sort((a, b) => (a.order || 0) - (b.order || 0));
      
      for (const [moduleIndex, module] of sortedModules.entries()) {
        checkPageBreak(40);
        
        // Module Header
        const moduleTitle = `Module ${module.order || moduleIndex + 1}: ${module.name}`;
        addWrappedText(moduleTitle, margin, yPosition, 12, 'bold', [0, 51, 102]);

        yPosition += 5;
        
        // Module Content Items
        if (module.content && module.content.length > 0) {
          // Sort content by order
          const sortedContent = [...module.content].sort((a, b) => (a.order || 0) - (b.order || 0));
          
          for (const [contentIndex, content] of sortedContent.entries()) {
            checkPageBreak(25);
            
            // Content Title with order
            const contentTitle = `${content.order || contentIndex + 1}. ${content.title}`;
            addWrappedText(contentTitle, margin + 10, yPosition, 10, 'bold', [51, 51, 51]);
            
            // Content Type
            const contentType = this.getContentTypeName(content.type);
            addWrappedText(`Type: ${contentType}`, margin + 15, yPosition, 8, 'italic', [128, 128, 128]);
            
            // Content URL if exists
            if (content.contentUrl && content.contentUrl.trim()) {
              const urlText = content.contentUrl.length > 60 
                ? content.contentUrl.substring(0, 60) + '...'
                : content.contentUrl;
              addWrappedText(`URL: ${urlText}`, margin + 15, yPosition, 8, 'normal', [0, 0, 255]);
            }
            
            // Text Content Preview
            if (content.textContent && content.textContent.trim()) {
              // Clean HTML tags and limit text length
              const cleanText = content.textContent
                .replace(/<[^>]*>/g, '') // Remove HTML tags
                .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                .trim();
              
              if (cleanText.length > 0) {
                const previewText = cleanText.length > 300 
                  ? cleanText.substring(0, 300) + '...'
                  : cleanText;
                addWrappedText(`Content Preview: ${previewText}`, margin + 15, yPosition, 8);
              }
            }
            
            // Files Information
            if (content.contentFiles && content.contentFiles.length > 0) {
              const fileNames = content.contentFiles
                .map(file => file.fileName)
                .join(', ');
              const filesText = fileNames.length > 80 
                ? fileNames.substring(0, 80) + '...'
                : fileNames;
              addWrappedText(`Files: ${filesText}`, margin + 15, yPosition, 8, 'italic', [102, 51, 153]);
            }
            
            yPosition += 8;
          }
        } else {
          addWrappedText('No content items in this module', margin + 10, yPosition, 9, 'italic', [153, 153, 153]);
        }
        
        yPosition += 10;
        
        // Add divider between modules (except for last module)
        if (moduleIndex < sortedModules.length - 1) {
          addDivider();
        }
      }
    } else {
      addWrappedText('No modules found in this course', margin, yPosition, 10, 'italic', [153, 153, 153]);
    }
    
    // Add footer with generation info on all pages
    const pageCount = doc.internal.getNumberOfPages();
   
    
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(128, 128, 128);
      
      // Footer text
      const footerText = `Page ${i} of ${pageCount}`;
      const footerWidth = doc.getStringUnitWidth(footerText) * 8 / doc.internal.scaleFactor;
      const footerX = (doc.internal.pageSize.width - footerWidth) / 2;
      
      doc.text(footerText, footerX, pageHeight - 10);
    }
    
    // Generate filename
    const safeTitle = this.course.title
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .toLowerCase();
    
    const fileName = `${safeTitle}_course_content.pdf`;
    
    // Save the PDF
    doc.save(fileName);
    
    // Show success message
    this.$toast.success('PDF generated successfully!');
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    this.$toast.error('Failed to generate PDF. Please try again.');
  } finally {
    // Hide loading state
    this.$store.dispatch("dashboard/cancelLoading");
  }
},

    handleNext() {
      if (!this.hasContent) return;

      if (this.isCourseCompletionPage) {
        return;
      }

      if (this.hasNextContent) {
        this.activeContentIndex = this.activeContentIndex + 1;
      }
    },

    handlePrevious() {
      if (!this.hasContent) return;

      if (this.isCourseCompletionPage) {
        this.activeContentIndex = this.allContent.length - 2; // Skip completion page
        return;
      }

      if (this.hasPreviousContent) {
        this.activeContentIndex = this.activeContentIndex - 1;
      }
    },

    navigateToAssessment() {
      // Replace with your actual route for assessment
      this.$router.push({
        name: "StartQuiz",
        params: { courseId: this.$route.params.courseId },
      });
    },

    resetForm() {
      this.newContent = {
        moduleId: null,
        title: "",
        contentUrl: "",
        textContent: "",
        type: null,
        description: "",
        files: [],
        uploadedFiles: [],
      };
      if (this.$refs.contentForm) {
        this.$refs.contentForm.reset();
      }
    },

    openImageFullScreen(file) {
      const url = `${this.config.hostname}/Resources/ModuleFiles/${file.filePath}`;
      window.open(url, '_blank');
    },

    closeContentDialog() {
      this.showAddContentDialog = false;
      this.resetForm();
      this.errorMessage = "";
    },

    removeUploadedFile(index) {
      this.newContent.uploadedFiles.splice(index, 1);
    },

    getUserRole() {
      return this.$store.getters["dashboard/userInfo"].roleName;
    },

    navigateToContent(content) {
      const index = this.allContent.findIndex(
        (c) => c.moduleContentId === content.moduleContentId
      );
      if (index !== -1) {
        this.activeContentIndex = index;
      }
    },

    isCurrentContent(content) {
      return this.currentContent?.moduleContentId === content.moduleContentId;
    },

    getFilesByType(type) {
      if (type === 'image') {
        return (
          this.currentContent?.contentFiles?.filter((file) =>
            file.contentType.startsWith("image/")
          ) ?? []
        );
      }
      return (
        this.currentContent?.contentFiles?.filter((file) =>
          file.contentType.startsWith(type)
        ) ?? []
      );
    },

    isPDF(filePath) {
      return filePath.toLowerCase().endsWith(".pdf");
    },

    isPowerPoint(filePath) {
      const ext = filePath.toLowerCase();
      return ext.endsWith(".ppt") || ext.endsWith(".pptx");
    },

    getContentTypeIcon(type) {
      const icons = {
        0: { icon: "mdi-video", color: "red" },
        1: { icon: "mdi-volume-high", color: "green" },
        2: { icon: "mdi-link", color: "blue" },
        3: { icon: "mdi-image", color: "purple" },
        4: { icon: "mdi-text", color: "grey" },
      };
      return icons[type] || { icon: "mdi-file", color: "grey" };
    },

    getYoutubeEmbedUrl(url) {
      if (!url) return "";
      const videoId = url.split("v=")[1];
      return `https://www.youtube.com/embed/${videoId}`;
    },

    validateFileType(file) {
      const allowedTypes = [
        "application/pdf",
        "video/mp4",
        "video/quicktime", // For .mov files
        "video/x-msvideo", // For .avi files
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        // Add image types
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/svg+xml",
        // Add audio types
        "audio/mpeg", // For .mp3 files
        "audio/wav", // For .wav files
        "audio/ogg", // For .ogg files
      ];

      // Special handling for content type "Image"
      if (this.newContent.type === 3) {
        const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
        return validImageTypes.includes(file.type);
        
      }

      // Special handling for content type "Audio"
      if (this.newContent.type === 1) {
        return file.type.startsWith("audio/");
      }

      return allowedTypes.includes(file.type);
    },

    resetFileUpload() {
      if (this.filePreview) {
        URL.revokeObjectURL(this.filePreview);
      }
      this.filePreview = null;
      this.selectedFile = null;
      this.previewFile = null;
      this.newContent.filePath = "";
      this.uploadError = "";
      this.isPDF = false;
      this.isVideo = false;
      this.newContent.file = null;
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    openContentModal(content) {
      this.selectedContent = content;
      this.contentModal = true;
    },
    getContentTypeName(type) {
      const types = {
        0: "Video",
        1: "Audio",
        2: "Link",
        3: "Image",
        4: "Text",
      };
      return types[type] || "Unknown Content";
    },

    beforeDestroy() {
      // Clean up any file previews
      this.resetFileUpload();
    },

    fetchCourseDetails() {
      const courseId = this.$route.params.courseId;
      if (!courseId) {
        this.$router.push({ name: "CourseList" });
        return;
      }

      this.$store
        .dispatch("course/fetchCourseById", courseId)
        .then((response) => {
          this.course = response.data;

          // When course is loaded, fetch associated quizzes
          const userEmail = this.userInfo.email;
          this.fetchCourseQuizzes({ courseId, email: userEmail });
        })
        .catch((error) => {
          console.error("Error fetching course:", error);
          this.$toast.error("Failed to load course content");
        });
    },

    fetchCourseQuizzes(obj) {
      // Dispatch action to fetch quizzes associated with this course
      this.$store
        .dispatch("course/fetchCourseQuizzes", obj)
        .then((response) => {
          this.availableQuizzes = response.data || [];
        })
        .catch((error) => {
          console.error("Error fetching course quizzes:", error);
          this.$toast.error("Failed to load assessments");
        });
    },

    

    showQuizInstructionsDialog(quiz) {
      // Store the current quiz for later use
      this.currentQuiz = quiz;

      // Show the instructions dialog
      this.quizInstructionsDialog = true;
    },

    continueQuiz() {
      // Close the instructions dialog
      this.quizInstructionsDialog = false;

      // Start the quiz with the stored quiz object
      if (this.currentQuiz) {
        this.startQuiz(this.currentQuiz);
      }
    },

    initializeQuiz(quiz) {
      this.$store.dispatch("dashboard/applyLoading");
      this.$store
        .dispatch("settings/fetchInstructions", quiz.quizTopicId)
        .then((response) => {
          this.$store.dispatch("dashboard/cancelLoading");

          if (response.data.length > 0) {
            // Store instructions and show dialog
            this.quizInstructions = response.data;
            this.showQuizInstructionsDialog(quiz);
          } else {
            // Directly start quiz if no instructions
            this.startQuiz(quiz);
          }
        })
        .catch((error) => {
          console.error("Error fetching quiz instructions:", error);
          this.$toast.error("Failed to load quiz instructions");
        });
    },

    startQuiz(quiz) {
      // Similar to dashboard's startQuiz method
      const quizInitData = {
        userId: parseInt(localStorage.getItem("loggedUserId")),
        email: this.$store.getters["dashboard/userInfo"].email,
        quizTopicId: quiz.quizTopicId,
        quizTitle: quiz.quizTitle,
        quizMark: quiz.quizTotalMarks,
        quizPassMarks: quiz.quizPassMarks,
        quizTime: quiz.quizTime,
        addedBy: parseInt(localStorage.getItem("loggedUserId")),
      };

      this.$store
        .dispatch("dashboard/createInitialResponse", quizInitData)
        .then((response) => {
          console.log("Quiz initialization response:", response);
          if (response.status === 200) {
            this.$store.dispatch("dashboard/startQuiz", quiz);
            this.$store.dispatch("dashboard/saveQuizLiveTime", quiz.quizTime);

            // Navigate to quiz page
            this.$router.push({ name: "StartQuiz" });
          } else {
            this.$toast.error(response.data.responseMsg);
            console.error("Error initializing quiz:", response);
          }
        })
        .catch((error) => {
          console.log("Error starting quiz:", error);
          this.$toast.error(error.status === 403 ? "You are not authorized to start this quiz" : "Failed to start quiz");
        });
    },

    openEditModule(module) {
      this.editingModule = {
        moduleId: module.moduleId,
        name: module.name,
      };
      this.showEditModuleDialog = true;
    },

    async updateModule() {
      if (!this.$refs.editModuleForm.validate()) return;

      this.isUpdatingModule = true;
      this.errorMessage = "";

      try {
        await this.$store.dispatch("course/updateModule", this.editingModule);
        await this.fetchCourseDetails();
        this.showEditModuleDialog = false;
        this.$toast.success("Module updated successfully");
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);
        this.errorMessage = errorMessage;
        this.$toast.error(`Failed to update module: ${errorMessage}`);
      } finally {
        this.isUpdatingModule = false;
      }
    },

    // Content Edit Methods
    openEditContent(content) {
      console.log("Content object:", content); // Add this to debug

      // Find the module this content belongs to
      const moduleId = content.moduleId;

      this.editingContent = {
        moduleContentId: content.moduleContentId,
        moduleId: moduleId, // Use the found moduleId
        title: content.title,
        contentUrl: content.contentUrl || "",
        textContent: content.textContent || "",
        type: content.type,
        contentFiles: [...(content.contentFiles || [])],
        newFiles: [],
        uploadedFiles: [],
      };

      console.log("Editing content moduleId:", this.editingContent.moduleId); // Debug
      this.showEditContentDialog = true;
    },

    async updateModuleContent() {
      if (!this.$refs.editContentForm.validate()) return;

      this.uploading = true;
      this.errorMessage = "";

      try {
        const contentData = {
          moduleContentId: this.editingContent.moduleContentId,
          moduleId: this.editingContent.moduleId,
          title: this.editingContent.title,
          type: this.editingContent.type,
          contentUrl: this.editingContent.contentUrl,
          textContent: this.editingContent.textContent,
          files: [
            ...this.editingContent.contentFiles,
            ...this.editingContent.uploadedFiles,
          ],
        };

        await this.$store.dispatch("course/updateModuleContent", contentData);
        await this.fetchCourseDetails();
        this.showEditContentDialog = false;
        this.$toast.success("Content updated successfully");
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);
        this.errorMessage = errorMessage;
        this.$toast.error(`Failed to update content: ${errorMessage}`);
      } finally {
        this.uploading = false;
      }
    },

    closeEditContentDialog() {
      this.showEditContentDialog = false;
      this.editingContent = {
        moduleContentId: null,
        moduleId: null,
        title: "",
        contentUrl: "",
        textContent: "",
        type: null,
        contentFiles: [],
        newFiles: [],
        uploadedFiles: [],
      };
      this.errorMessage = "";
    },

    // Delete Methods
    openDeleteDialog(type, id) {
      this.deleteType = type;
      this.deleteItemId = id;
      this.showDeleteDialog = true;
    },

    async confirmDelete() {
      this.isDeleting = true;
      try {
        if (this.deleteType === "module") {
          await this.$store.dispatch("course/deleteModule", this.deleteItemId);
        } else if (this.deleteType === "content") {
          await this.$store.dispatch(
            "course/deleteModuleContent",
            this.deleteItemId
          );
        }
        await this.fetchCourseDetails();
        this.$toast.success(`${this.deleteType} deleted successfully`);
        this.showDeleteDialog = false;
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);
        this.$toast.error(
          `Failed to delete ${this.deleteType}: ${errorMessage}`
        );
      } finally {
        this.isDeleting = false;
      }
    },

    async removeContentFile(file) {
      try {
        await this.$store.dispatch("course/deleteContentFile", file.id);
        this.editingContent.contentFiles =
          this.editingContent.contentFiles.filter((f) => f.id !== file.id);
        this.$toast.success("File removed successfully");
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);
        this.$toast.error(`Failed to remove file: ${errorMessage}`);
      }
    },
  },
};
</script>

<style scoped>
.border-r {
  border-right: 1px solid #e0e0e0;
}

.video-wrapper {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  height: 0;
  overflow: hidden;
}

.video-frame {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.pdf-frame {
  width: 100%;
  height: 800px;
  border: none;
}

.content-wrapper {
  max-width: 100%;
  overflow-x: hidden;
}

.course-completion-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 500px;
  padding: 2rem;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.assessment-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  width: 100%;
}

.assessment-grid .v-card {
  min-width: 250px;
  width: 100%;
  max-width: 300px;
}

.v-list-item__icon {
  margin-right: 16px !important;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.course-completion-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 600px;
  padding: 2rem;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.image-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 300px;
}

.image-frame {
  max-width: 100%;
  max-height: 600px;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.audio-wrapper {
  margin-bottom: 16px;
}

.audio-player {
  width: 100%;
  margin-bottom: 8px;
}
</style>
